import { useRef, useEffect, MutableRefObject } from 'react'

type GetRefFn = (ref: HTMLInputElement) => void

interface StateManagerChildrenProps {
  readonly setRefBotProtection: GetRefFn
  readonly setRefSyslog: GetRefFn
  readonly setRefSampling: GetRefFn
}

interface ShowData {
  readonly previous?: boolean
  readonly current: boolean
}

export interface EditShowData {
  readonly botProtection: ShowData
  readonly sampling: ShowData
  readonly syslog: ShowData
}

// Stores refs to HTML input elements and calls focus() on them when the condition is correct
export function ServerSettingsFormBulkFocusManager({
  editShowData,
  children
}: {
  readonly editShowData: EditShowData
  readonly children: (props: StateManagerChildrenProps) => any
}) {
  type InputRefsMap = Record<
    'botProtection' | 'syslog' | 'sampling',
    MutableRefObject<null | HTMLInputElement>
  >

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const inputRefs: InputRefsMap = {
    botProtection: useRef<null | HTMLInputElement>(null),
    syslog: useRef<null | HTMLInputElement>(null),
    sampling: useRef<null | HTMLInputElement>(null)
  }

  useEffect(() => {
    const focusInput = (
      stateKey: keyof EditShowData,
      refKey: keyof InputRefsMap
    ) => {
      const editShowVal = editShowData[stateKey].previous
      if (
        typeof editShowVal !== 'undefined' &&
        editShowVal &&
        !editShowData[stateKey].current
      ) {
        const ref = inputRefs[refKey]
        if (ref.current) {
          ref.current.focus()
        }
      }
    }
    focusInput('botProtection', 'botProtection')
    focusInput('syslog', 'syslog')
    focusInput('sampling', 'sampling')
  }, [editShowData, inputRefs])

  return children({
    setRefBotProtection: (ref: HTMLInputElement) =>
      (inputRefs.botProtection.current = ref),
    setRefSyslog: (ref: HTMLInputElement) => (inputRefs.syslog.current = ref),
    setRefSampling: (ref: HTMLInputElement) =>
      (inputRefs.sampling.current = ref)
  })
}
